import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
} from '@mui/material';
import {
  Edit,
  Delete,
  Search,
  Add,
  Visibility,
  VisibilityOff,
  RestoreFromTrash,
  FilterList,
  DeleteForever,
  CloudSync
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { Product, ProductFormData } from '../../types';
import { useAuth } from '../../context/AuthContext';

export const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Obtener usuario actual
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false); // Separate loading for search
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showOutlet, setShowOutlet] = useState(false);
  const [sortBy, setSortBy] = useState<'original' | 'sku' | 'codigo' | 'color' | 'grupo' | 'categoria' | 'descripcion'>('original');
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // 50 productos por página por defecto
  
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; product: Product | null; permanent: boolean }>({
    open: false,
    product: null,
    permanent: false
  });
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);

  // Estados para sincronización con Google Sheets
  const [syncDialog, setSyncDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    success: boolean;
    stats: { total: number; created: number; updated: number; deactivated: number; errors: number };
    error?: string;
  } | null>(null);

  // Helper function to format dimensions (all in meters)
  const formatDimensions = (product: Product) => {
    const dimensions = [];
    if (product.largo) dimensions.push(`L:${product.largo}m`);
    if (product.ancho) dimensions.push(`A:${product.ancho}m`);
    if (product.espesor) dimensions.push(`E:${product.espesor}m`);
    
    if (dimensions.length === 0) return '-';
    
    // Si hay más de una dimensión, mostrarlas en líneas separadas
    if (dimensions.length > 1) {
      return (
        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          {dimensions.map((dim, index) => (
            <Typography key={index} variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2, fontFamily: 'monospace' }}>
              {dim}
            </Typography>
          ))}
        </Box>
      );
    }
    
    // Si solo hay una dimensión, mostrarla directamente
    return (
      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>
        {dimensions[0]}
      </Typography>
    );
  };

  // Función para ordenar productos
  const sortProducts = (products: Product[], sortBy: string): Product[] => {
    // Si es orden original, devolver los productos sin ordenar
    if (sortBy === 'original') {
      return products;
    }

    return [...products].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortBy) {
        case 'sku':
          aValue = a.sku || '';
          bValue = b.sku || '';
          break;
        case 'codigo':
          aValue = a.codigo || '';
          bValue = b.codigo || '';
          break;
        case 'color':
          aValue = a.color || '';
          bValue = b.color || '';
          break;
        case 'grupo':
          aValue = a.grupo || '';
          bValue = b.grupo || '';
          break;
        case 'categoria':
          aValue = a.categoria || '';
          bValue = b.categoria || '';
          break;
        case 'descripcion':
          aValue = a.descripcion || '';
          bValue = b.descripcion || '';
          break;
        default:
          aValue = a.sku || '';
          bValue = b.sku || '';
      }

      // Ordenamiento alfanumérico
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
      }
      
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    });
  };

  // Productos ordenados
  const sortedProducts = sortProducts(products, sortBy);
  
  // Cálculos de paginación
  const totalProducts = sortedProducts.length;
  const totalPages = Math.ceil(totalProducts / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);

  // Reset página cuando cambia la búsqueda o filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, showInactive, showOutlet, sortBy, itemsPerPage]);

  useEffect(() => {
    loadProducts(true); // Initial load
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      loadProducts(false); // Search load
    }
  }, [debouncedSearchTerm, showInactive, showOutlet]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadProducts = async (isInitialLoad = false) => {
    try {
      // Only show main loading spinner on initial load
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setSearchLoading(true);
      }
      setError(null);
      
      const filters = {
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
        ...(showOutlet ? { outlet_only: true } : {}),
        ...(showInactive && !showOutlet ? { inactive_only: true } : {}),
        ...((!showInactive && !showOutlet) ? { active_only: true } : {}),
        limit: 1000  // Solicitar hasta 1000 productos
      };
      const response = await apiService.products.getAll(filters);
      
      // Verificar estructura de respuesta
      if (response && Array.isArray(response.items)) {
        setProducts(response.items);
      } else if (Array.isArray(response)) {
        // Si la respuesta es directamente un array
        setProducts(response);
      } else {
        console.warn('Unexpected response structure:', response);
        setProducts([]);
      }
    } catch (err: any) {
      console.error('Error loading products:', err);
      setError('Error al cargar los productos. Verifique que el backend esté funcionando.');
      setProducts([]); // Asegurar que products no sea undefined
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset página al buscar
  };

  const handleEdit = (product: Product) => {
    navigate(`/products/${product.id}/edit`);
  };

  const handleDeletePermanently = (product: Product) => {
    setDeleteDialog({ open: true, product, permanent: true });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.product) return;

    try {
      await apiService.products.deletePermanently(deleteDialog.product.id);
      // Eliminación permanente siempre quita el producto de la lista
      setProducts(products.filter(p => p.id !== deleteDialog.product!.id));
      setDeleteDialog({ open: false, product: null, permanent: false });
    } catch (err: any) {
      console.error('Error deleting product:', err);
      setError('Error al eliminar el producto');
    }
  };

  const handleToggleActive = async (product: Product) => {
    try {
      const updatedData: ProductFormData = {
        codigo: product.codigo,
        sku: product.sku || '',
        color: product.color,
        grupo: product.grupo,
        categoria: product.categoria,
        stock: product.stock,
        largo: product.largo,
        ancho: product.ancho,
        espesor: product.espesor,
        precio_unitario: product.precio_unitario,
        precio_m2: product.precio_m2,
        estructura_m2: product.estructura_m2,
        is_active: !product.is_active
      };
      
      await apiService.products.update(product.id, updatedData);
      
      // Actualizar el producto en la lista local
      setProducts(products.map(p => 
        p.id === product.id ? { ...p, is_active: !p.is_active } : p
      ));
    } catch (err: any) {
      console.error('Error updating product:', err);
      setError('Error al actualizar el producto');
    }
  };

  const handleReactivate = async (product: Product) => {
    try {
      const updatedData: ProductFormData = {
        codigo: product.codigo,
        sku: product.sku || '',
        color: product.color,
        grupo: product.grupo,
        categoria: product.categoria,
        stock: product.stock,
        largo: product.largo,
        ancho: product.ancho,
        espesor: product.espesor,
        precio_unitario: product.precio_unitario,
        precio_m2: product.precio_m2,
        estructura_m2: product.estructura_m2,
        is_active: true
      };
      
      await apiService.products.update(product.id, updatedData);
      
      // Actualizar el producto en la lista local
      setProducts(products.map(p => 
        p.id === product.id ? { ...p, is_active: true } : p
      ));
    } catch (err: any) {
      console.error('Error reactivating product:', err);
      setError('Error al reactivar el producto');
    }
  };

  const handleCreateProduct = () => {
    navigate('/products/new');
  };

  const handleDeleteAll = () => {
    setDeleteAllDialog(true);
  };

  const confirmDeleteAll = async () => {
    try {
      setLoading(true);
      await apiService.products.deleteAll();
      setProducts([]);
      setDeleteAllDialog(false);
      setError(null);
    } catch (err: any) {
      console.error('Error deleting all products:', err);
      setError('Error al borrar todos los productos');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromGoogleSheets = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      
      // 1. Cargar configuración desde la BD
      const settings = await apiService.get<Array<{ key: string; value: string; category: string }>>('/settings/', {
        params: { category: 'google_sheets' }
      });
      
      const spreadsheetId = settings.find(s => s.key === 'products_spreadsheet_id')?.value;
      const rangeName = settings.find(s => s.key === 'products_range_name')?.value;
      const headerRow = parseInt(settings.find(s => s.key === 'products_header_row')?.value || '0');
      
      if (!spreadsheetId || !rangeName) {
        setError('La configuración de Google Sheets no está completa. Contacta al administrador.');
        setSyncing(false);
        return;
      }
      
      // 2. Validar la estructura de la hoja
      const validation = await apiService.get<{ valid: boolean; error?: string }>('/google-sheets/validate-sheet', {
        params: { spreadsheet_id: spreadsheetId, range_name: rangeName, header_row: headerRow }
      });
      
      if (!validation.valid) {
        setError(`Error de validación: ${validation.error}`);
        setSyncing(false);
        return;
      }
      
      // 3. Mostrar diálogo de confirmación
      setSyncDialog(true);
      setSyncing(false); // Liberar el estado de carga para habilitar el botón del diálogo
      
    } catch (err: any) {
      console.error('Error preparing sync:', err);
      setError(err.response?.data?.detail || 'Error al preparar la sincronización');
      setSyncing(false);
    }
  };

  const confirmSync = async () => {
    try {
      setSyncing(true);
      setSyncDialog(false);
      
      // Cargar configuración nuevamente
      const settings = await apiService.get<Array<{ key: string; value: string; category: string }>>('/settings/', {
        params: { category: 'google_sheets' }
      });
      
      const spreadsheetId = settings.find(s => s.key === 'products_spreadsheet_id')?.value;
      const rangeName = settings.find(s => s.key === 'products_range_name')?.value;
      const headerRow = parseInt(settings.find(s => s.key === 'products_header_row')?.value || '0');
      
      // 4. Ejecutar sincronización
      const result = await apiService.post<{
        success: boolean;
        stats: { total: number; created: number; updated: number; deactivated: number; errors: number };
        error?: string;
      }>('/google-sheets/sync-products', null, {
        params: {
          spreadsheet_id: spreadsheetId,
          range_name: rangeName,
          header_row: headerRow,
          dry_run: false
        }
      });
      
      setSyncResult(result);
      
      // 5. Recargar productos
      await loadProducts();
      
    } catch (err: any) {
      console.error('Error syncing products:', err);
      setSyncResult({
        success: false,
        error: err.response?.data?.detail || 'Error al sincronizar productos',
        stats: { total: 0, created: 0, updated: 0, deactivated: 0, errors: 0 }
      });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (

    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* Encabezado y filtros en Card */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(60,72,100,0.10)' }}>
        <CardContent>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} gap={3}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Gestión de Stock
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totalProducts > 0 ? (
                  <>
                    Mostrando {startIndex + 1}-{Math.min(endIndex, totalProducts)} de {totalProducts} producto{totalProducts !== 1 ? 's' : ''}
                    {totalPages > 1 && ` (Página ${currentPage} de ${totalPages})`}
                  </>
                ) : (
                  'No hay productos para mostrar'
                )}
                {searchTerm && ` (filtrado por: "${searchTerm}")`}
              </Typography>
            </Box>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudSync />}
                onClick={handleSyncFromGoogleSheets}
                disabled={syncing}
                sx={{ boxShadow: '0 2px 8px 0 rgba(66,133,244,0.08)', borderRadius: 2 }}
              >
                {syncing ? 'Sincronizando...' : 'Actualizar desde Google Sheets'}
              </Button>
              {user?.role === 'admin' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForever />}
                  onClick={handleDeleteAll}
                  disabled={products.length === 0}
                  sx={{ boxShadow: '0 2px 8px 0 rgba(239,68,68,0.08)', borderRadius: 2 }}
                >
                  Borrar Todos
                </Button>
              )}
              {user?.role === 'admin' && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreateProduct}
                  sx={{ boxShadow: '0 2px 8px 0 rgba(66,154,45,0.12)', borderRadius: 2, display: 'none' }}
                >
                  Nuevo Producto
                </Button>
              )}
            </Box>
          </Box>
          {/* Filtros */}
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mt={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar productos por código, descripción..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searchLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Search />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: '220px', background: '#f8fafc', borderRadius: 2 }}
            />
            <FormControl sx={{ minWidth: '160px' }} size="small">
              <InputLabel>Ordenar por</InputLabel>
              <Select
                value={sortBy}
                label="Ordenar por"
                onChange={(e) => {
                  setSortBy(e.target.value as any);
                  setCurrentPage(1);
                }}
              >
                <MenuItem value="original">Orden original (Google Sheets)</MenuItem>
                <MenuItem value="sku">SKU</MenuItem>
                <MenuItem value="codigo">Código</MenuItem>
                <MenuItem value="color">Color</MenuItem>
                <MenuItem value="grupo">Grupo</MenuItem>
                <MenuItem value="categoria">Categoría</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: '120px' }} size="small">
              <InputLabel>Por página</InputLabel>
              <Select
                value={itemsPerPage}
                label="Por página"
                onChange={(e) => {
                  setItemsPerPage(e.target.value as number);
                  setCurrentPage(1);
                }}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
                <MenuItem value={500}>Todos</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant={showOutlet ? "contained" : "outlined"}
              startIcon={<FilterList />}
              onClick={() => {
                setShowOutlet(!showOutlet);
                setCurrentPage(1);
              }}
              sx={{ minWidth: '160px', borderRadius: 2 }}
            >
              {showOutlet ? 'Mostrar Todos' : 'Outlet'}
            </Button>
            <Button
              variant={showInactive ? "contained" : "outlined"}
              startIcon={<FilterList />}
              onClick={() => {
                setShowInactive(!showInactive);
                setCurrentPage(1);
              }}
              sx={{ minWidth: '160px', borderRadius: 2 }}
            >
              {showInactive ? 'Mostrar Activos' : 'Solo Inactivos'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}



  <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 24px 0 rgba(60,72,100,0.10)' }}>
        <Table>
          <TableHead sx={{ backgroundColor: 'rgb(66,154,45)' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SKU</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>CODIGO</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>GRUPO</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>CAT.</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>DESCRIPCIÓN</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>COLOR</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>STOCK</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>DIMENSIONES (m)</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>PRECIO UNITARIO</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>PRECIO X M2</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>DESCUENTO (%)</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>ESTRUCTURA POR M2</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
              {user?.role === 'admin' && (
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts && paginatedProducts.length > 0 ? paginatedProducts.map((product, index) => (
              <TableRow 
                key={product.id}
                sx={{ 
                  backgroundColor: index % 2 === 0 ? 'white' : '#f5f5f5',
                  transition: 'background 0.2s',
                  '&:hover': {
                    backgroundColor: '#e0f2fe' // celeste muy claro
                  }
                }}
              >
                <TableCell>{product.sku || '-'}</TableCell>
                <TableCell align="center">{product.codigo}</TableCell>
                <TableCell align="center">{product.grupo || '-'}</TableCell>
                <TableCell align="center">{product.categoria || '-'}</TableCell>
                  <TableCell align="center">{product.descripcion || '-'}</TableCell>
                  <TableCell align="center">{product.color || '-'}</TableCell>
                <TableCell align="center">{product.stock || 0}</TableCell>
                <TableCell align="center">
                  {formatDimensions(product)}
                </TableCell>
                <TableCell align="center">
                  {product.currency || 'USD'} {product.precio_unitario?.toLocaleString() || '0'}
                </TableCell>
                <TableCell align="center">
                  {product.precio_m2 ? `${product.currency || 'USD'} ${product.precio_m2.toLocaleString()}` : '-'}
                </TableCell>
                <TableCell align="center">
                  {product.descuento ? `${product.descuento}%` : '0%'}
                </TableCell>
                <TableCell align="center">
                  {product.estructura_m2 && product.estructura_m2.trim() ? (
                    <Box sx={{ maxWidth: 200, overflow: 'auto', textAlign: 'left', mx: 'auto' }}>
                      {product.estructura_m2.split('\n').map((line, idx) => {
                        const [codigo, cantidad] = line.split(',');
                        return (
                          <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                            {codigo ? codigo.trim() : ''}{cantidad ? ` × ${cantidad.trim()}` : ''}
                          </Typography>
                        );
                      })}
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={product.is_active ? 'Activo' : 'Inactivo'}
                    color={product.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                {user?.role === 'admin' && (
                  <TableCell align="center">
                    {product.is_active ? (
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(product)}
                        color="warning"
                        title="Desactivar producto"
                      >
                        <VisibilityOff />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => handleReactivate(product)}
                        color="success"
                        title="Reactivar producto"
                      >
                        <RestoreFromTrash />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={user?.role === 'admin' ? 12 : 11} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No hay productos para mostrar
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Controles de paginación */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2" color="text.secondary">
            {startIndex}-{endIndex} de {sortedProducts.length} productos (Página {currentPage} de {totalPages})
          </Typography>
        </Box>
      )}

      {(!products || products.length === 0) && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron productos
          </Typography>
        </Box>
      )}

      {/* Dialog de confirmación para eliminar */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, product: null, permanent: false })}
      >
        <DialogTitle>Confirmar eliminación permanente</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar PERMANENTEMENTE el producto "{deleteDialog.product?.codigo}"? 
            Esta acción no se puede deshacer y el producto se eliminará completamente de la base de datos.
            Los presupuestos existentes mantendrán la información del producto.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, product: null, permanent: false })}>
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Eliminar Permanentemente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para borrar todos */}
      <Dialog
        open={deleteAllDialog}
        onClose={() => setDeleteAllDialog(false)}
      >
        <DialogTitle>
          Confirmar eliminación de todos los productos
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar TODOS los productos? Esta acción eliminará permanentemente {products.length} producto{products.length !== 1 ? 's' : ''} de la base de datos y no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={confirmDeleteAll} color="error" variant="contained">
            Eliminar Todos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para borrar todos los productos */}
      <Dialog
        open={deleteAllDialog}
        onClose={() => setDeleteAllDialog(false)}
      >
        <DialogTitle>
          Confirmar eliminación de todos los productos
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar TODOS los productos? Esta acción eliminará permanentemente {products.length} producto{products.length !== 1 ? 's' : ''} de la base de datos y no se puede deshacer.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            ⚠️ Esta acción es irreversible
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={confirmDeleteAll} color="error" variant="contained">
            Eliminar Todos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para sincronizar */}
      <Dialog
        open={syncDialog}
        onClose={() => !syncing && setSyncDialog(false)}
      >
        <DialogTitle>
          Sincronizar productos desde Google Sheets
        </DialogTitle>
        <DialogContent>
          <Typography>
            Esta acción sincronizará los productos desde la hoja de cálculo de Google configurada.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            • Los productos nuevos serán creados
          </Typography>
          <Typography variant="body2">
            • Los productos existentes serán actualizados
          </Typography>
          <Typography variant="body2">
            • Los productos no presentes en la hoja serán desactivados
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2, fontWeight: 'bold' }}>
            ℹ️ Esta operación puede tomar unos segundos
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialog(false)} disabled={syncing}>
            Cancelar
          </Button>
          <Button onClick={confirmSync} color="primary" variant="contained" disabled={syncing}>
            Sincronizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de resultado de sincronización */}
      <Dialog
        open={syncResult !== null}
        onClose={() => setSyncResult(null)}
      >
        <DialogTitle>
          {syncResult?.success ? 'Sincronización completada' : 'Error en la sincronización'}
        </DialogTitle>
        <DialogContent>
          {syncResult?.success ? (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                La sincronización se completó exitosamente:
              </Typography>
              <Typography variant="body2">
                • Total procesados: {syncResult.stats.total}
              </Typography>
              <Typography variant="body2" color="success.main">
                • Creados: {syncResult.stats.created}
              </Typography>
              <Typography variant="body2" color="info.main">
                • Actualizados: {syncResult.stats.updated}
              </Typography>
              <Typography variant="body2" color="warning.main">
                • Desactivados: {syncResult.stats.deactivated}
              </Typography>
              {syncResult.stats.errors > 0 && (
                <Typography variant="body2" color="error.main">
                  • Errores: {syncResult.stats.errors}
                </Typography>
              )}
            </>
          ) : (
            <Typography color="error">
              {syncResult?.error || 'Ocurrió un error durante la sincronización'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncResult(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAB para crear producto en móvil */}
      {user?.role === 'admin' && (
        <Fab
          color="success"
          aria-label="add"
          onClick={handleCreateProduct}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', sm: 'none' }
          }}
        >
          <Add />
        </Fab>
      )}
    </Box>
  );
};
